import { prop, formatDate, getSymbolFromCurrency } from '.';
import { __, __r } from 'react-i18n';

function getSearchProductInfo(product, goodName = 'main_good') {
  const currency = prop(product, `${goodName}.currency_id`);
  const price = prop(product, `${goodName}.final_price`);
  const priceWithoutVat = prop(product, `${goodName}.final_price_without_vat`);
  const unit = prop(product, `${goodName}.units`) || 'ks';
  const url = `/${prop(product, 'url', prop(product, 'product_publish_url'))}`;
  const { name, picture, product_id: id } = product;

  return {
    currency,
    price: price ? price.toFixed(2) : '',
    priceWithoutVat: priceWithoutVat ? priceWithoutVat.toFixed(2) : '',
    unit,
    url,
    id,
    name,
    picture: picture
      ? `${process.env.REACT_APP_API_BASE_URL}/image?query=${picture}&width=32&height=32&webp=0&type=cover`
      : '/images/zemplin/placeholder.svg',
  };
}

function getSearchCategoryInfo(category) {
  const {
    category_name,
    url,
    category_id,
    counts,
    parent_categories,
  } = category;
  const lastParent =
    parent_categories && parent_categories.length > 0
      ? parent_categories[parent_categories.length - 1]
      : null;

  return {
    name: category_name,
    url: url ? url : 'url',
    id: category_id,
    counts,
    lastParent,
  };
}

function getProductSmallInfo(product) {
  const { picture } = product;
  return {
    picture: picture
      ? `${process.env.REACT_APP_API_BASE_URL}/image?query=${picture}&width=80&height=80&webp=0`
      : '/images/zemplin/placeholder.svg',
  };
}

function getProductDefaultInfo(product, apiUrl?) {
  const currency = getSymbolFromCurrency(
    prop(product, 'main_good.currency_id', 'EUR'),
  );
  const ordernr = prop(product, 'main_good.order_number', '');
  const onStock = prop(product, 'main_good.on_stock_count', 0);
  const availid = prop(product, 'main_good.availability.avail_id', 11);
  const availability = prop(
    product,
    'main_good.availability.avail_uniqid',
    'Nedostupné',
  );
  const oldPrice = prop(
    product,
    'main_good.oldprice',
    prop(product, 'main_good.unitprice'),
  );
  const price = prop(product, 'main_good.final_price');
  const priceWithoutVat = prop(product, 'main_good.price_without_vat');
  const unit = prop(product, 'main_good.units') || 'ks';
  const url = `/${product.url}`;
  const {
    name,
    picture,
    product_id: id,
    issaleout,
    is_new,
    is_sale: issale,
    is_sale_b2b,
  } = product;
  const productId = prop(product, 'product_id');
  const goodId = prop(product, 'main_good.good_id');
  const photogallery = prop(product, 'photogallery');

  return {
    currency,
    ordernr,
    onStock,
    availid,
    availability,
    oldPrice,
    price,
    priceWithoutVat,
    unit,
    url,
    id,
    name,
    issaleout,
    is_new,
    issale,
    issaleb2b: is_sale_b2b,
    productId,
    goodId,
    picture: picture
      ? `${process.env.REACT_APP_API_BASE_URL}/image?query=${picture}&width=160&height=160&webp=0`
      : '/images/zemplin/placeholder.svg',
    pictureBig: picture
      ? `${process.env.REACT_APP_API_BASE_URL}/image?query=${picture}&width=500&height=500&webp=0`
      : '/images/zemplin/placeholder.svg',
    photogallery,
  };
}

function getCartProductInfo(product, apiUrl?: string) {
  const currency = prop(product, 'good.currency_id', 'EUR');
  const ordernr = prop(product, 'good.order_number', '');
  const onStock = prop(product, 'good.on_stock_count', 0);
  const count = prop(product, 'count');
  const productNote = prop(product, 'product_note');
  const unit = prop(product, 'good.units', 'ks');
  const sumPrice = prop(product, 'sum_price', 0);
  const plu = prop(product, 'product.plu', null);

  const {
    name,
    picture,
    product_id: id,
    issaleout,
    isnew,
    issale,
  } = product.product;
  const productId = prop(product, 'product.product_id');
  const goodId = prop(product, 'good.good_id');
  const url = `/${product.product.url}`;
  const availability = prop(product, 'availability', null);

  return {
    count,
    currency,
    ordernr,
    onStock,
    sumPrice: sumPrice ? sumPrice.toFixed(2) : 0,
    url,
    id,
    name,
    issaleout,
    is_new: isnew,
    issale,
    productId,
    goodId,
    productNote,
    unit,
    availability,
    plu,
    picture: picture
      ? `${
          apiUrl ? apiUrl : `${process.env.REACT_APP_API_BASE_URL}`
        }/image?query=${picture}&width=160&height=160&webp=0`
      : '/images/zemplin/placeholder.svg',
  };
}

function getArticleDefaultInfo(article) {
  const url = `/${article.url}`;
  const { name: title, image: picture } = article;
  const date = formatDate(prop(article, 'created_date', ''), 'DD.MM.YYYY');

  return {
    url,
    title,
    date,
    picture: picture
      ? `${process.env.REACT_APP_API_BASE_URL}/image?query=${picture}&width=328&height=248&webp=0`
      : '/images/zemplin/placeholder.svg',
  };
}

function getArticleDetailInfo(article) {
  const title = prop(article, 'name', '');
  const date = formatDate(prop(article, 'created_date', ''), 'DD.MM.YYYY');
  const lastUpdate = formatDate(prop(article, 'last_update', ''), 'DD.MM.YYYY');
  const category = prop(article, 'category', '');
  const content = prop(article, 'json_content.body', '');
  const downloads = prop(article, 'downloads', []);

  return {
    title,
    date,
    category,
    content,
    downloads,
    lastUpdate,
  };
}

function getInvoiceDetailInfo(invoice) {
  const invoiceNr = prop(invoice, 'invoiceNr', '');
  const sumPrice = prop(invoice, 'sumPrice', '');
  const invoicePath = prop(invoice, 'invoicePath', null);
  const invoiceFileName = prop(invoice, 'invoiceFileName', '');
  const notPaid = prop(invoice, 'notPaid');

  return {
    invoiceNr,
    sumPrice,
    invoicePath,
    invoiceFileName,
    notPaid,
  };
}

function getOrderRowInfo(order) {
  const ordernr = prop(order, 'order_nr', '');
  const status = prop(order, 'order_status_readable', '');
  const sumPrice = prop(order, 'order_tprice', '');
  const date = formatDate(
    prop(order, 'created_datetime', null),
    'DD.MM.YYYY HH:MM:SS',
  );
  const id = prop(order, 'id', '');
  const publicId = prop(order, 'public_id', '');
  const url = `${__r(
    'routes:moj-ucet/objednavky',
    '/moj-ucet/objednavky',
  )}/${publicId}`;

  return {
    id,
    date,
    status,
    sumPrice: sumPrice ? sumPrice.toFixed(2) : '',
    ordernr,
    url,
  };
}

function getRequestRowInfo(request) {
  const requestNr = prop(request, 'order_nr', '');
  const yourNr = prop(request, 'demand_number', '-') || '-';
  const statusReadable = prop(request, 'order_status_readable', '');
  const status = prop(request, 'order_status', '');
  const date = formatDate(
    prop(request, 'created_datetime', null),
    'DD.MM.YYYY HH:MM:SS',
  );
  const id = prop(request, 'order_id', '');
  const publicId = prop(request, 'public_id', '');
  const url = `${__r(
    'routes:moj-ucet/dopyty',
    '/moj-ucet/dopyty',
  )}/${publicId}`;

  return {
    id,
    date,
    status,
    statusReadable,
    requestNr,
    yourNr,
    url,
  };
}

function getRequestDetailInfo(request) {
  const vatRate = prop(request, 'vat_rate', 20);
  const orderId = prop(request, 'order_id', '');
  const vs = prop(request, 'vs', '-') || '-';
  const yourNr = prop(request, 'demand_number', '-') || '-';
  let orderStatusReadable = prop(request, 'order_status_readable', '');
  const items = prop(request, 'items', '');
  const totalPrice = prop(request, 'order_tprice', 0);
  const totalPriceWithoutVat = prop(request, 'order_tprice_without_vat', 0);
  const currency = getSymbolFromCurrency(
    prop(request, 'order_tpricecurrency', 'EUR'),
  );
  const vat = totalPrice - totalPriceWithoutVat;
  const deliveryName = prop(request, 'delivery.delivery_typename');
  const paymentName = prop(request, 'payment.payment_typename');
  const deliveryPrice = prop(request, 'delivery.delivery_price');
  const paymentPrice = prop(request, 'payment.payment_price');
  const ordernr = prop(request, 'order_nr', '');
  const note = prop(request, 'order_note');
  const deliveryPriceWithoutVat = deliveryPrice
    ? deliveryPrice / (1 + vatRate / 100)
    : 0;
  const paymentPriceWithoutVat = paymentPrice
    ? paymentPrice / (1 + vatRate / 100)
    : 0;

  const orderStatus = prop(request, 'order_status', '');
  if (orderStatus === 'NEW') {
    orderStatusReadable = 'Prijaté';
  } else if (orderStatus === 'INPROGRESS') {
    orderStatusReadable = 'Spracované';
  } else if (orderStatus === 'SHIPPED') {
    orderStatusReadable = 'Objednávka';
  }

  return {
    ordernr,
    orderId,
    orderStatus,
    vs,
    orderStatusReadable,
    items,
    yourNr,
    totalPrice: totalPrice ? totalPrice.toFixed(2) : 0,
    totalPriceWithoutVat: totalPriceWithoutVat
      ? totalPriceWithoutVat.toFixed(2)
      : 0,
    vat: vat ? vat.toFixed(2) : 0,
    currency,
    deliveryPrice,
    paymentPrice,
    deliveryPriceWithoutVat,
    paymentPriceWithoutVat,
    note,
    deliveryName,
    paymentName,
  };
}

function getRequestDetailItemInfo(item) {
  const quantity = prop(item, 'quantity', 0);
  const units = prop(item, 'units', 'ks');
  const ordernr = prop(item, 'ordernr', '');
  const name = prop(item, 'product_name', '');
  const note = prop(item, 'product_note', '');
  const currency = getSymbolFromCurrency(prop(item, 'currency', 'EUR'));
  const totalPrice = prop(item, 'totalprice', 0);
  const totalPriceWithoutVat = prop(item, 'totalprice_without_vat', 0);
  const unitprice = prop(item, 'unitprice', 0);
  const vatRate = prop(item, 'vat_rate', 20);
  const picture = prop(item, 'product_picture');
  const image = picture
    ? `${process.env.REACT_APP_API_BASE_URL}/image?query=${picture}&width=48&height=48&webp=0`
    : '/images/zemplin/placeholder.svg';
  const unitpriceWithoutVat = unitprice ? unitprice / (1 + vatRate / 100) : 0;
  const pdp = prop(item, 'good_details_json.pdp');
  const availabilityData = prop(item, 'good_details_json.availabilityData');

  return {
    availabilityData,
    quantity,
    ordernr,
    name,
    note,
    image,
    currency,
    totalPriceWithoutVat: totalPriceWithoutVat
      ? totalPriceWithoutVat.toFixed(2)
      : 0,
    totalPrice: totalPrice ? totalPrice.toFixed(2) : 0,
    unitprice: unitprice ? unitprice : 0,
    unitpriceWithoutVat,
    pdp,
    picture,
    units,
  };
}

function getDeliveryAddressInfo(address) {
  const deliveryStreet = prop(address, 'delivery_street', '');
  const deliveryNumber = prop(address, 'delivery_number', '');
  const deliveryZip = prop(address, 'delivery_zip', '');
  const deliveryCity = prop(address, 'delivery_city', '');
  const country =
    prop(address, 'country', '') === 'sk'
      ? 'Slovensko'
      : prop(address, 'country', '');
  const id = prop(address, 'id', '');
  const isDefault = prop(address, 'isDefault', false);

  return {
    deliveryStreet,
    deliveryNumber,
    deliveryZip,
    deliveryCity,
    country,
    id,
    isDefault,
  };
}

function getAccountRowInfo(account) {
  const id = prop(account, 'id', '');
  const name = prop(account, 'meno', '');
  const surname = prop(account, 'priezvisko', '');
  const email = prop(account, 'email', '');
  const orders = prop(account, 'orders', 0);
  const requests = prop(account, 'requests', 0);

  return {
    id,
    name,
    surname,
    email,
    orders,
    requests,
  };
}

function getCartSummaryInfo(data, user?: any) {
  const isB2B = user && user.b2b;
  const totalPrice = prop(data, 'total_price', 0);
  const totalItemsPrice = prop(data, 'total_items_price', 0);
  const totalItemsPriceWithoutVat = prop(
    data,
    'total_items_price_without_vat',
    0,
  );
  const totalPriceWithoutVat = prop(data, 'total_price_without_vat', 0);
  const vatTotal = totalPrice - totalPriceWithoutVat;
  const vatItems = totalItemsPrice - totalItemsPriceWithoutVat;
  const currency = getSymbolFromCurrency(prop(data, 'currency', 'EUR'));
  const deliveryPrice = isB2B
    ? prop(data, 'delivery.price.priceWithoutVat', 0)
    : prop(data, 'delivery.price.price', 0);
  const paymentPrice = isB2B
    ? prop(data, 'payment.price.priceWithoutVat', 0)
    : prop(data, 'payment.price.price', 0);

  return {
    totalPrice: totalPrice ? totalPrice.toFixed(2) : 0,
    totalPriceWithoutVat: totalPriceWithoutVat
      ? totalPriceWithoutVat.toFixed(2)
      : 0,
    vatTotal: vatTotal ? vatTotal.toFixed(2) : 0,
    currency,
    deliveryPrice,
    paymentPrice,
    totalItemsPrice: totalItemsPrice ? totalItemsPrice.toFixed(2) : 0,
    totalItemsPriceWithoutVat: totalItemsPriceWithoutVat
      ? totalItemsPriceWithoutVat.toFixed(2)
      : 0,
    vatItems: vatItems ? vatItems.toFixed(2) : 0,
  };
}

function getCartProductAvailabilityInfo(product, countInCart, onStock) {
  const availText =
    onStock > 0
      ? onStock < countInCart
        ? __('Čiastočne skladom')
        : __('Skladom')
      : __('Nedostupné');

  const color =
    availText === __('Čiastočne skladom')
      ? '#FFA454'
      : availText === 'Skladom'
      ? '#2DCC8D'
      : '#FF2D58';
  return { availText, color, onStock };
}

export const resolveCategoryUrl = (
  id: string | number,
  url: string,
): string => {
  const editedUrl =
    url.lastIndexOf('/') > 0
      ? url.slice(url.lastIndexOf('/') + 1, url.length)
      : url;
  return `/kategoria/${id}/${editedUrl ? editedUrl : 'url'}`;
};

export const resolveCategoryShortUrl = (
  id: string | number,
  url: string,
): string => {
  return `/${url ? url : ''}`;
};

export const resolveProductUrl = (id: string | number, url: string): string => {
  return `/${url ? url : ''}`;
};

export const getProductDetailInfo = (product, apiUrl?) => {
  const publish = prop(product, 'publish', [])[0];
  const good = prop(product, 'goods', [])[0];
  const attribs = prop(product, 'attribs', []);
  const downloads = prop(publish, 'content.documents', []);
  const currency = prop(product, 'main_good.currency.name', '') ? '€' : '€';
  const inPackage = prop(publish, 'content.json_content.description', '');
  const description = prop(publish, 'content.json_content.body', '');
  const ordernr = prop(
    product,
    'main_good.order_number',
    prop(product, 'product_plu', ''),
  );

  const shortDescription = prop(
    publish,
    'content.json_content.short_description',
    '',
  );
  const onStock = prop(product, 'main_good.on_stock_count', 0);
  const plu = prop(product, 'product_plu', '');
  const ean = prop(product, 'product_ean', '');
  const availid = prop(product, 'main_good.availability.avail_id', 11);
  const availability = prop(
    product,
    'main_good.availability.avail_uniqid',
    'Nedostupné',
  );
  const oldPrice = prop(
    product,
    'main_good.oldprice',
    prop(product, 'main_good.final_price'),
  );
  const price = prop(product, 'main_good.final_price');
  const unit = prop(good, 'units') || 'ks';

  const url = `/${product.url}`;
  const {
    name,
    picture,
    product_id: id,
    issaleout,
    is_new,
    isnew,
    is_sale,
    issale,
  } = product;
  const brand = prop(product, 'brand.brand_name');
  const sale = prop(product, 'main_good.sale');
  const salePercentage = prop(product, 'main_good.sale_percentage');
  const isSaleB2b = prop(product, 'is_sale_b2b');
  const variants = prop(product, 'variants', []);
  const goods = prop(product, 'goods', []);

  const categories = prop(product, 'parent_categories', []);
  // const nameWithGroup = `${
  //   categories.length > 0 ? prop(categories[0], 'category_name', '') : ''
  // } ${name}`;

  const imageName = url
    ? url.substring(url.lastIndexOf('/') + 1)
    : 'placeholder';
  return {
    attribs,
    downloads,
    currency,
    inPackage,
    shortDescription,
    description,
    ordernr,
    onStock,
    ean,
    oldPrice: oldPrice ? oldPrice.toFixed(2) : 0,
    price: price ? price.toFixed(2) : 0,
    unit,
    url,
    id,
    issaleout,
    isnew: isnew || is_new,
    issale: issale || is_sale,
    name,
    picture,
    image: picture
      ? `${
          apiUrl ? apiUrl : `${process.env.REACT_APP_API_BASE_URL}`
        }/image-product-name/${imageName}?query=${picture}&width=160&height=160&webp=1&watermark=1`
      : '/images/zlatapriadka/placeholder.svg',
    brand,
    sale,
    salePercentage,
    isSaleB2b,
    variants,
    goods,
  };
};

export const getElasticCategoryProductInfo = product => {
  const priceWithoutVat = prop(product, 'main_good_price');
  const unit = prop(product, 'main_good_units') || 'ks';
  const currency = prop(product, 'product_detail.main_good.currency_id', 'EUR');

  const onStock = prop(product, 'main_good_stock_count', 0);
  const availability =
    prop(product, 'availability', 0) === 1 ? 'Skladom' : 'Nedostupné';
  const oldPrice = prop(
    product,
    'main_good.oldprice',
    prop(product, 'main_good.unitprice'),
  );
  const price = prop(product, 'main_good_price');
  const {
    name,
    picture,
    product_id: id,
    product_publish_url,
    is_new,
    is_sale,
  } = product;
  const isSale = prop(product, 'product_detail.issale');
  const isNew = prop(product, 'product_detail.isnew');
  const isSaleB2b = prop(product, 'product_detail.is_sale_b2b');
  const issaleout = prop(
    product,
    'issaleout',
    prop(product, 'product_detail.issaleout'),
  );
  const url = `/${product_publish_url}`;
  const goodId = prop(product, 'main_good_id');
  const description = prop(product, 'description');

  return {
    onStock,
    availability,
    oldPrice,
    price,
    priceWithoutVat,
    unit,
    url,
    productId: id,
    name,
    issaleout,
    is_new,
    issale: isSale || is_sale,
    is_sale_b2b: isSaleB2b,
    isnew: isNew || is_new,
    goodId,
    ordernr: '',
    currency,
    picture: picture
      ? `${process.env.REACT_APP_API_BASE_URL}/image?query=${picture}&width=160&height=160&webp=0`
      : '/images/zemplin/placeholder.svg',
    pictureBig: picture
      ? `${process.env.REACT_APP_API_BASE_URL}/image?query=${picture}&width=500&height=500&webp=0`
      : '/images/zemplin/placeholder.svg',
    description,
  };
};

const getDownloadFileInfo = download => {
  const id = prop(download, 'document_id', '');
  const path = prop(download, 'document_path');
  const name = prop(download, 'document_name', '');
  const fullPath = `${process.env.REACT_APP_API_BASE_URL}/file?query=${path}`;

  return { id, name, path: fullPath };
};

const getFinishedOrderInfo = order => {
  const orderNr = prop(order, 'order_nr', '');
  const name = prop(order, 'person.name');
  const surname = prop(order, 'person.surname', '');
  const phone = prop(order, 'person.phone', '');
  const paymentType = prop(order, 'payment.payment_type');
  const orderPaidDatetime = prop(order, 'order_paid_datetime');

  return {
    orderNr,
    name,
    surname,
    paymentType,
    phone,
    orderPaidDatetime,
    ...getOrderDeliveryInfo(order),
  };
};

const getOrderDeliveryInfo = order => {
  const city = prop(
    order,
    'delivery.delivery_addr_city',
    prop(order, 'address.city'),
  );
  const street = prop(
    order,
    'person.delivery_addr_street',
    prop(order, 'address.street'),
  );
  const streetNumber = prop(
    order,
    'person.delivery_addr_street_number',
    prop(order, 'address.street_number'),
  );
  const zip = prop(
    order,
    'person.delivery_addr_zip',
    prop(order, 'address.zip'),
  );
  const country = prop(
    order,
    'person.delivery_addr_country',
    prop(order, 'address.country'),
  );

  return { city, country, streetNumber, street, zip };
};

const getCategoryIdsFromProduct = (product: any): string[] => {
  const pCategories = prop(product, 'parent_categories', []);
  const uniqueCatIds: string[] = [];
  pCategories.map(p => uniqueCatIds.push(p.category_number));
  return uniqueCatIds;
};

const getCartItemData = item => {
  const { idx, count, sum_price: price } = item;
  const name = prop(item, `product.name`, '');
  const url = `/${prop(item, `product.url`)}`;
  const picture = prop(item, `product.picture`, null);
  const currency = prop(item, `good.currency_id`, 'EUR');
  const attrib = '';

  return {
    idx,
    count,
    price,
    url,
    currency,
    attrib,
    name,
    picture: picture
      ? `${process.env.REACT_APP_API_BASE_URL}/image?query=${picture}&width=48&height=48&webp=0&type=cover`
      : '/images/zemplin/placeholder.svg',
  };
};

const getColorFromName = name => {
  switch (name) {
    case 'červená':
      return 'red';
    case 'modrá':
      return 'darkblue';
    case 'žltá':
      return 'gold';
    case 'čierna':
      return 'black';
    case 'béžová':
      return 'beige';
    case 'biela':
      return 'white';
    case 'fialová':
      return 'purple';
    case 'hnedá':
      return 'brown';
    case 'horčicová':
      return 'goldenrod';
    case 'jeans':
      return 'cornflowerblue';
    case 'lososová':
      return 'salmon';
    case 'maslová':
      return 'lemonchiffon';
    case 'modrofialová':
      return 'blueviolet';
    case 'oranžová':
      return 'orange';
    case 'rúžová':
      return 'pink';
    case 'sivá':
      return 'gray';
    case 'smotanová':
      return 'wheat';
    case 'staroružová':
      return '#E38F8F';
    case 'terakota':
      return 'peru';
    case 'zelená':
      return 'green';
    default:
      return '';
  }
};

export {
  getSearchProductInfo,
  getSearchCategoryInfo,
  getProductDefaultInfo,
  getArticleDefaultInfo,
  getArticleDetailInfo,
  getInvoiceDetailInfo,
  getOrderRowInfo,
  getRequestRowInfo,
  getDeliveryAddressInfo,
  getAccountRowInfo,
  getDownloadFileInfo,
  getFinishedOrderInfo,
  getProductSmallInfo,
  getCartProductInfo,
  getCartSummaryInfo,
  getCartProductAvailabilityInfo,
  getRequestDetailInfo,
  getRequestDetailItemInfo,
  getCategoryIdsFromProduct,
  getCartItemData,
  getColorFromName,
};
